// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
/* eslint-disable no-use-before-define */
import {
  amber,
  blue,
  blueGrey,
  brown,
  cyan,
  deepOrange,
  deepPurple,
  green,
  grey,
  indigo,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
} from '@mui/material/colors';
import { type Theme as MUITheme, createTheme, responsiveFontSizes } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { STEP_NAME } from '@seedplanner/shared-utils';

interface SeedPlannerTheme extends MUITheme {
  custom: Custom;
}

export type { SeedPlannerTheme as Theme };

declare module '@mui/material/styles' {
  interface Theme {
    custom: Custom;
  }
  interface PaletteOptions {
    custom: {
      primary: {
        light3: string;
        light2: string;
        light1: string;
        light: string;
        main: string;
        dark: string;
        dark1: string;
        dark2: string;
        dark3: string;
      };
      secondary: {
        light3: string;
        light2: string;
        light1: string;
        light: string;
        main: string;
        dark: string;
        dark1: string;
        dark2: string;
        dark3: string;
      };
    };
  }
  interface ThemeOptions {
    custom: Custom;
  }
}

type ColorMode = MUITheme['palette']['mode'];
export const PRIMARY_MAIN = '#003153';
export const SECONDARY_MAIN = '#9ec745';
export const FONT_FAMILY =
  'ui-sans-serif,system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"';
// Used this page for colors: https://mui.com/material-ui/customization/color/
// Also see: https://zenoo.github.io/mui-theme-creator/
export default function createSeedPlannerTheme(colorMode: ColorMode): SeedPlannerTheme {
  const lightDivider = 'rgba(0, 0, 0, 0.075)';
  const custom = {
    lightDivider,
    containerDownMDPad,
    containerUpMDPad,
    containerDownMDPaddingTop,
    containerUpMDPaddingTop,
    titleDownMDNoHorizontalPad,
    titleUpMDNoHorizontalPad,
    titleDownMD,
    titleUpMD,
    comment: {
      borderTop: `1px solid ${lightDivider}`,
      paddingBottom: 16,
      paddingTop: 16,
    },
    tasks: {
      [STEP_NAME.SOAK]: {
        dark: blue[700],
        light: blue[300],
      },
      [STEP_NAME.PLANT]: {
        dark: brown[500],
        light: brown[200],
      },
      [STEP_NAME.WEIGHTED_GERMINATION]: {
        dark: lime[900],
        light: lime[400],
      },
      [STEP_NAME.PLANT_AND_WEIGHTED_GERM]: {
        dark: lime[900],
        light: lime[400],
      },
      [STEP_NAME.UNWEIGHTED_GERMINATION]: {
        dark: lime[900],
        light: lime[400],
      },
      [STEP_NAME.PLANT_AND_UNWEIGHTED_GERM]: {
        dark: lime[900],
        light: lime[400],
      },
      [STEP_NAME.WATERING]: {
        dark: cyan[700],
        light: cyan[400],
      },
      [STEP_NAME.BLACKOUT]: {
        dark: indigo[500],
        light: indigo[200],
      },
      [STEP_NAME.PLANT_AND_BLACKOUT]: {
        dark: indigo[500],
        light: indigo[200],
      },
      [STEP_NAME.PLANT_AND_HUMIDITY_DOME]: {
        dark: teal[500],
        light: teal[300],
      },
      [STEP_NAME.HUMIDITY_DOME]: {
        dark: teal[500],
        light: teal[300],
      },
      [STEP_NAME.UNDER_LIGHTS]: {
        dark: orange[800],
        light: orange[600],
      },
      [STEP_NAME.HARVEST]: {
        dark: lightGreen[700],
        light: lightGreen[500],
      },
    },
    colors: {
      amber,
      blue,
      blueGrey,
      brown,
      cyan,
      deepOrange,
      deepPurple,
      green,
      grey,
      indigo,
      lightBlue,
      lightGreen,
      lime,
      orange,
      pink,
      purple,
      red,
      teal,
      yellow,
      common: {
        black: '#000',
        white: '#fff',
        oddRow: colorMode === 'dark' ? 'rgb(42, 42, 42)' : lightDivider,
        offwhite: '#f6f9fc', // AlgoExpert background white
        transparent: 'rgba(0, 0, 0, 0)',
        fullBlack: 'rgba(0, 0, 0, 1)',
        darkBlack: 'rgba(0, 0, 0, 0.87)',
        lightBlack: 'rgba(0, 0, 0, 0.54)',
        minBlack: 'rgba(0, 0, 0, 0.26)',
        faintBlack: 'rgba(0, 0, 0, 0.12)',
        fullWhite: 'rgba(255, 255, 255, 1)',
        darkWhite: 'rgba(255, 255, 255, 0.87)',
        lightWhite: 'rgba(255, 255, 255, 0.54)',
      },
    },
  };
  const theme = createTheme({
    components: {
      MuiDialog: {
        styleOverrides: {
          paper: {
            margin: '32px 8px',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          outlined: {
            borderColor: colorMode === 'dark' ? '#fff' : undefined,
            color: colorMode === 'dark' ? '#fff' : undefined,
            '& .MuiSvgIcon-root': {
              color: colorMode === 'dark' ? '#fff' : undefined,
            },
          },
          text: {
            color: colorMode === 'dark' ? '#fff' : undefined,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            '& .MuiButton-textPrimary': {
              color: colorMode === 'dark' ? '#fff' : undefined,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            '&.MuiInputLabel-outlined': {
              color: colorMode === 'dark' ? '#fff' : undefined,
              '&.Mui-focused': {
                color: colorMode === 'dark' ? '#fff' : undefined,
              },
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: colorMode === 'dark' ? '#fff' : undefined,
            '&.Mui-checked': {
              color: colorMode === 'dark' ? '#fff' : undefined,
            },
          },
        },
      },
    },
    palette: {
      custom: {
        // https://hihayk.github.io/scale/#4/4/54/54/2/10/7/7/9ec745/158/199/69/white
        secondary: {
          light3: '#DEE7A7',
          light2: '#D0E08E',
          light1: '#C1D876',
          light: '#B0D05D',
          main: SECONDARY_MAIN,
          dark: '#8AAD3B',
          dark1: '#769331',
          dark2: '#617827',
          dark3: '#4C5E1E',
        },
        // https://hihayk.github.io/scale/#4/4/54/54/2/10/7/7/003153/0/49/83/white
        primary: {
          light3: '#8AA7B1',
          light2: '#678B9A',
          light1: '#456E83',
          light: '#22506B',
          main: PRIMARY_MAIN,
          dark: '#002B48',
          dark1: '#00253E',
          dark2: '#001F33',
          dark3: '#001828',
        },
      },
      mode: colorMode,
      primary: {
        light: '#335A75',
        main: PRIMARY_MAIN,
        dark: '#00223A',
      },
      secondary: {
        light: '#B1D26A',
        main: SECONDARY_MAIN,
        dark: '#6E8B30',
      },
      error: red,
      background: {
        default: colorMode === 'dark' ? '#121212' : grey[200],
      },
    },
    typography: {
      fontFamily: FONT_FAMILY,
    },
    custom,
    shape: {
      borderRadius: 4,
    },
  });

  return responsiveFontSizes(theme) as SeedPlannerTheme;
}

const containerDownMDPad = {
  paddingLeft: 16,
  paddingRight: 16,
};
const containerDownMDPaddingTop = 16;
const titleDownMDNoHorizontalPad = {
  paddingBottom: 16,
  paddingTop: 16,
};
const titleDownMD = {
  ...titleDownMDNoHorizontalPad,
  ...containerDownMDPad,
};
const containerUpMDPad = {
  paddingLeft: 24,
  paddingRight: 24,
};
const containerUpMDPaddingTop = 24;
const titleUpMDNoHorizontalPad = {
  paddingBottom: 24,
  paddingTop: 24,
};
const titleUpMD = {
  ...titleUpMDNoHorizontalPad,
  ...containerUpMDPad,
};

interface Color {
  // biome-ignore lint/complexity/useLiteralKeys: <explanation>
  ['50']?: string;
  // biome-ignore lint/complexity/useLiteralKeys: <explanation>
  ['100']?: string;
  // biome-ignore lint/complexity/useLiteralKeys: <explanation>
  ['200']?: string;
  // biome-ignore lint/complexity/useLiteralKeys: <explanation>
  ['300']?: string;
  // biome-ignore lint/complexity/useLiteralKeys: <explanation>
  ['400']?: string;
  // biome-ignore lint/complexity/useLiteralKeys: <explanation>
  ['500']?: string;
  // biome-ignore lint/complexity/useLiteralKeys: <explanation>
  ['600']?: string;
  // biome-ignore lint/complexity/useLiteralKeys: <explanation>
  ['700']?: string;
  // biome-ignore lint/complexity/useLiteralKeys: <explanation>
  ['800']?: string;
  // biome-ignore lint/complexity/useLiteralKeys: <explanation>
  ['900']?: string;
  A100?: string;
  A200?: string;
  A400?: string;
  A700?: string;
  contrastDefaultColor?: string;
}

interface Custom {
  containerDownMDPad: {
    paddingLeft: number;
    paddingRight: number;
  };
  containerUpMDPad: {
    paddingLeft: number;
    paddingRight: number;
  };
  containerDownMDPaddingTop: number;
  containerUpMDPaddingTop: number;
  titleDownMDNoHorizontalPad: {
    paddingBottom: number;
    paddingTop: number;
  };
  titleUpMDNoHorizontalPad: {
    paddingBottom: number;
    paddingTop: number;
  };
  titleDownMD: {
    paddingBottom: number;
    paddingTop: number;
    paddingLeft: number;
    paddingRight: number;
  };
  titleUpMD: {
    paddingBottom: number;
    paddingTop: number;
    paddingLeft: number;
    paddingRight: number;
  };
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  comment: any;
  tasks: {
    [key: string]: {
      dark: string;
      light: string;
    };
  };
  colors: {
    amber: Color;
    blue: Color;
    blueGrey: Color;
    brown: Color;
    cyan: Color;
    deepOrange: Color;
    deepPurple: Color;
    green: Color;
    grey: Color;
    indigo: Color;
    lightBlue: Color;
    lightGreen: Color;
    lime: Color;
    orange: Color;
    pink: Color;
    purple: Color;
    red: Color;
    teal: Color;
    yellow: Color;
    common: {
      oddRow: string;
      black: string;
      white: string;
      offwhite: string;
      transparent: string;
      fullBlack: string;
      darkBlack: string;
      lightBlack: string;
      minBlack: string;
      faintBlack: string;
      fullWhite: string;
      darkWhite: string;
      lightWhite: string;
    };
  };
}
